import {
  Chess,
  Swal,
  createPlayableBoard,
  refreshPlayableBoard,
  renderMoveList
} from './chess-ui.js';
import { saveNotebookTransfer } from './game-notebook-transfer.js';
import { validatePosition } from './position-validation.js';

const root = document.querySelector('[data-analysis]');
if (root) initializeAnalysisStudio(root);

function initializeAnalysisStudio(root) {
  const initialDefault = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  const requested = root.dataset.initialFen?.trim();
  const validation = requested ? validatePosition(requested) : validatePosition(initialDefault);
  const initialFen = validation.isLegal ? validation.normalizedFen : initialDefault;
  const stockfishUrl = root.dataset.stockfishUrl || '/vendor/stockfish/stockfish-18-lite-single.js';
  const game = new Chess(initialFen);
  const positions = [game.fen()];
  const moves = [];
  let currentPly = 0;
  let orientation = 'white';

  const movesElement = root.querySelector('[data-analysis-moves]');
  const fenInput = root.querySelector('[data-analysis-fen]');
  const plyIndicator = root.querySelector('[data-analysis-ply]');
  const turnElement = root.querySelector('[data-analysis-turn]');
  const engineStatus = root.querySelector('[data-engine-status]');
  const engineLinesElement = root.querySelector('[data-engine-lines]');
  const engineScore = root.querySelector('[data-main-eval]');
  const evalScore = root.querySelector('[data-eval-score]');
  const evalFill = root.querySelector('.eval-fill');
  const engineToggle = root.querySelector('[data-toggle-analysis-engine]');
  const depthSelect = root.querySelector('[data-engine-depth]');
  const positionLabel = root.querySelector('[data-analysis-position-label]');

  let engine = null;
  let engineReady = false;
  let engineEnabled = true;
  let analysisTimer = 0;
  let analysisSequence = 0;
  let pendingAnalysis = null;
  let activeAnalysis = null;
  const engineLines = new Map();

  const ground = createPlayableBoard(root.querySelector('#analysisBoard'), game, move => {
    if (currentPly < moves.length) {
      moves.splice(currentPly);
      positions.splice(currentPly + 1);
    }
    moves.push(move);
    positions.push(game.fen());
    currentPly = moves.length;
    refreshPlayableBoard(ground, game, !game.isGameOver(), [move.from, move.to]);
    updateInterface();
    scheduleAnalysis();
  }, { orientation });

  bind('[data-analysis-start]', () => showPly(0));
  bind('[data-analysis-prev]', () => showPly(currentPly - 1));
  bind('[data-analysis-next]', () => showPly(currentPly + 1));
  bind('[data-analysis-end]', () => showPly(moves.length));
  bind('[data-analysis-flip]', () => {
    orientation = orientation === 'white' ? 'black' : 'white';
    ground.set({ orientation });
  });
  bind('[data-load-analysis-fen]', loadFen);
  bind('[data-reset-analysis]', () => { fenInput.value = initialDefault; loadFen(); });
  bind('[data-clear-variation]', clearVariation);
  bind('[data-toggle-analysis-engine]', toggleEngine);
  bind('[data-toggle-fen-tools]', () => root.querySelector('[data-fen-tools]').classList.toggle('hidden'));
  bind('[data-analysis-copy-fen]', copyFen);
  bind('[data-save-analysis-notebook]', saveToNotebook);
  depthSelect.addEventListener('change', () => scheduleAnalysis(true));
  window.addEventListener('beforeunload', () => { engine?.postMessage('stop'); engine?.terminate(); });
  document.addEventListener('keydown', event => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
    if (event.key === 'ArrowLeft') showPly(currentPly - 1);
    if (event.key === 'ArrowRight') showPly(currentPly + 1);
    if (event.key === 'Home') showPly(0);
    if (event.key === 'End') showPly(moves.length);
  });

  ensureEngine();
  updateInterface();
  scheduleAnalysis(true);

  if (requested && !validation.isLegal) {
    Swal.fire({ icon: 'error', title: 'پوزیسیون بارگذاری نشد', text: validation.errors[0] || 'FEN نامعتبر است.', confirmButtonText: 'باشه' });
  }

  function bind(selector, handler) { root.querySelector(selector)?.addEventListener('click', handler); }

  function showPly(ply) {
    const bounded = Math.max(0, Math.min(Number(ply), moves.length));
    if (bounded === currentPly) return;
    currentPly = bounded;
    game.load(positions[currentPly]);
    const move = currentPly > 0 ? moves[currentPly - 1] : null;
    refreshPlayableBoard(ground, game, !game.isGameOver(), move ? [move.from, move.to] : undefined);
    updateInterface();
    scheduleAnalysis();
  }

  function updateInterface() {
    fenInput.value = game.fen();
    renderMoveList(movesElement, moves, currentPly, showPly);
    plyIndicator.textContent = currentPly === 0 ? 'شروع پوزیسیون' : `حرکت ${toPersianNumber(Math.ceil(currentPly / 2))} ${currentPly % 2 ? 'سفید' : 'سیاه'}`;
    turnElement.textContent = game.turn() === 'w' ? 'نوبت سفید' : 'نوبت سیاه';
    positionLabel.textContent = currentPly === moves.length ? 'ارزیابی پوزیسیون جاری' : `آنالیز حرکت ${toPersianNumber(Math.ceil(currentPly / 2))}`;
    root.querySelector('[data-analysis-start]').disabled = currentPly === 0;
    root.querySelector('[data-analysis-prev]').disabled = currentPly === 0;
    root.querySelector('[data-analysis-next]').disabled = currentPly >= moves.length;
    root.querySelector('[data-analysis-end]').disabled = currentPly >= moves.length;
  }

  async function loadFen() {
    const result = validatePosition(fenInput.value.trim());
    if (!result.isLegal) {
      await Swal.fire({ icon: 'error', title: 'پوزیسیون قابل آنالیز نیست', html: `<div class="position-error-list">${result.errors.map(x => `<div>• ${escapeHtml(x)}</div>`).join('')}</div>`, confirmButtonText: 'باشه' });
      return;
    }
    game.load(result.normalizedFen);
    moves.splice(0);
    positions.splice(0, positions.length, game.fen());
    currentPly = 0;
    refreshPlayableBoard(ground, game, !game.isGameOver());
    updateInterface();
    scheduleAnalysis(true);
  }

  function clearVariation() {
    moves.splice(0);
    positions.splice(1);
    currentPly = 0;
    game.load(positions[0]);
    refreshPlayableBoard(ground, game, !game.isGameOver());
    updateInterface();
    scheduleAnalysis(true);
  }

  async function copyFen() {
    try {
      await navigator.clipboard.writeText(game.fen());
      Swal.fire({ icon: 'success', title: 'FEN کپی شد', showConfirmButton: false, timer: 1200 });
    } catch {
      fenInput.classList.remove('hidden');
      fenInput.select();
    }
  }

  function saveToNotebook() {
    saveNotebookTransfer({
      kind: 'analysis',
      title: moves.length > 0 ? 'شاخه آنالیز شخصی' : 'پوزیسیون تمرینی',
      initialFen: positions[0],
      moves: moves.map(move => ({
        uci: `${move.from}${move.to}${move.promotion || ''}`,
        san: move.san
      }))
    });
    const url = new URL(root.dataset.notebookEditUrl, location.origin);
    url.searchParams.set('import', 'analysis');
    url.searchParams.set('method', positions[0] === initialDefault ? 'board' : 'fen');
    location.href = url.toString();
  }

  function toggleEngine() {
    engineEnabled = !engineEnabled;
    engineToggle.classList.toggle('active', engineEnabled);
    engineToggle.textContent = engineEnabled ? 'توقف' : 'فعال‌سازی';
    if (!engineEnabled) {
      stopAnalysis();
      engineStatus.textContent = 'آنالیز خاموش است';
      renderPlaceholder('برای بررسی پوزیسیون، آنالیز را فعال کن.');
      return;
    }
    ensureEngine();
    scheduleAnalysis(true);
  }

  function ensureEngine() {
    if (engine) return;
    engine = new Worker(stockfishUrl);
    engine.addEventListener('message', event => handleEngineMessage(String(event.data)));
    engine.addEventListener('error', () => {
      engineReady = false;
      engineEnabled = false;
      engineToggle.classList.remove('active');
      engineToggle.textContent = 'تلاش دوباره';
      engineStatus.textContent = 'موتور بارگذاری نشد';
      renderPlaceholder('فایل Stockfish در مرورگر اجرا نشد.');
      engine = null;
    });
    engine.postMessage('uci');
  }

  function handleEngineMessage(message) {
    if (message === 'uciok') {
      engine.postMessage('setoption name MultiPV value 3');
      engine.postMessage('isready');
      return;
    }
    if (message === 'readyok') {
      engineReady = true;
      if (engineEnabled && pendingAnalysis) startPendingAnalysis();
      return;
    }
    if (!engineEnabled || !activeAnalysis) return;
    if (message.startsWith('bestmove ')) {
      engineStatus.textContent = `کامل شد • عمق ${activeAnalysis.depth}`;
      return;
    }
    if (!message.startsWith('info ') || !message.includes(' pv ')) return;
    const multipv = Number(matchValue(message, / multipv (\d+)/) ?? 1);
    const depth = Number(matchValue(message, / depth (\d+)/) ?? 0);
    const cp = matchValue(message, / score cp (-?\d+)/);
    const mate = matchValue(message, / score mate (-?\d+)/);
    const pv = matchValue(message, / pv (.+)$/);
    if (!pv) return;
    const score = mate !== null ? { type: 'mate', value: Number(mate) } : { type: 'cp', value: Number(cp ?? 0) };
    engineLines.set(multipv, { depth, score, pv });
    renderEngineLines();
  }

  function scheduleAnalysis(immediate = false) {
    if (!engineEnabled) return;
    clearTimeout(analysisTimer);
    analysisTimer = window.setTimeout(queueAnalysis, immediate ? 0 : 130);
  }

  function queueAnalysis() {
    if (!engineEnabled) return;
    ensureEngine();
    pendingAnalysis = { sequence: ++analysisSequence, fen: game.fen(), depth: Number(depthSelect.value), ply: currentPly };
    activeAnalysis = null;
    engineLines.clear();
    engineScore.textContent = '…';
    evalScore.textContent = '…';
    engineStatus.textContent = engineReady ? 'در حال تغییر پوزیسیون…' : 'در حال بارگذاری موتور…';
    renderPlaceholder('در حال محاسبه بهترین ادامه‌ها…');
    if (!engineReady) return;
    engine.postMessage('stop');
    engine.postMessage('isready');
  }

  function startPendingAnalysis() {
    if (!engineEnabled || !pendingAnalysis) return;
    activeAnalysis = pendingAnalysis;
    pendingAnalysis = null;
    engineLines.clear();
    engineStatus.textContent = `در حال آنالیز • عمق ${activeAnalysis.depth}`;
    engine.postMessage(`position fen ${activeAnalysis.fen}`);
    engine.postMessage(`go depth ${activeAnalysis.depth}`);
  }

  function stopAnalysis() {
    clearTimeout(analysisTimer);
    pendingAnalysis = null;
    activeAnalysis = null;
    engineLines.clear();
    engine?.postMessage('stop');
  }

  function renderEngineLines() {
    if (!activeAnalysis) return;
    engineLinesElement.replaceChildren();
    const position = new Chess(activeAnalysis.fen);
    for (const [, line] of [...engineLines.entries()].sort((a, b) => a[0] - b[0])) {
      const item = document.createElement('div');
      item.className = 'pgn-engine-line';
      const score = document.createElement('strong');
      score.textContent = formatEvaluation(line.score, position.turn());
      const continuation = document.createElement('span');
      continuation.textContent = pvToSan(line.pv, activeAnalysis.fen);
      continuation.title = continuation.textContent;
      item.append(score, continuation);
      engineLinesElement.append(item);
    }
    const primary = engineLines.get(1);
    if (primary) {
      const text = formatEvaluation(primary.score, position.turn());
      engineScore.textContent = text;
      engineStatus.textContent = `عمق ${primary.depth} • سه ادامه برتر`;
      updateEvalRail(primary.score, position.turn(), text);
    }
  }

  function updateEvalRail(score, turn, text) {
    const whiteValue = turn === 'w' ? score.value : -score.value;
    const percent = score.type === 'mate' ? (whiteValue > 0 ? 96 : 4) : 50 + 45 * (2 / (1 + Math.exp(-whiteValue / 220)) - 1);
    evalFill.style.height = `${Math.max(4, Math.min(96, percent))}%`;
    evalScore.textContent = text;
    evalScore.classList.toggle('on-white', percent > 50);
  }

  function renderPlaceholder(text) {
    engineLinesElement.replaceChildren();
    const placeholder = document.createElement('div');
    placeholder.className = 'engine-placeholder';
    placeholder.textContent = text;
    engineLinesElement.append(placeholder);
  }
}

function matchValue(text, expression) { return text.match(expression)?.[1] ?? null; }
function toPersianNumber(value) { return new Intl.NumberFormat('fa-IR', { useGrouping: false }).format(Number(value) || 0); }
function formatEvaluation(score, turn) {
  const white = turn === 'w' ? score.value : -score.value;
  if (score.type === 'mate') return `${white < 0 ? '-' : ''}M${Math.abs(white)}`;
  return `${white >= 0 ? '+' : ''}${(white / 100).toFixed(2)}`;
}
function pvToSan(pv, fen) {
  const replay = new Chess(fen); const sans = [];
  for (const uci of pv.split(' ').slice(0, 14)) {
    try { const move = replay.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] }); if (!move) break; sans.push(move.san); } catch { break; }
  }
  return sans.join(' ');
}
function escapeHtml(value) { return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;'); }
